<template>
    <Modal2
        @cancel="$emit('close')"
        :show-footer="false"
        :title="$t('message.sendTemplateMessage')"
        className="meta-template-message-modal"
    >
        <div class="meta-template-modal">
            <div class="modal-body">
                <!-- Left: Preview -->
                <div class="preview-section">
                    <WhatsappMessagePreview
                        :templateType="previewTemplateType"
                        :headerMediaType="previewHeaderMediaType"
                        :headerMediaURL="previewHeaderMediaURL"
                        :headerText="previewHeaderText"
                        :body="previewBody"
                        :footer="previewFooter"
                        :buttons="previewButtons"
                        :cards="previewCards"
                        :limitedTimeOffer="previewLimitedTimeOffer"
                    />
                </div>

                <!-- Right: Configuration -->
                <div class="config-section">
                    <!-- Template Meta -->
                    <div class="meta-block">
                        <div class="meta-item">
                            <span class="meta-label">Template</span>
                            <span class="meta-value">{{ template.name }}</span>
                        </div>
                        <div class="meta-item">
                            <span class="meta-label">Category</span>
                            <span class="meta-value meta-tag">{{ template.category }}</span>
                        </div>
                        <div class="meta-item">
                            <span class="meta-label">Language</span>
                            <span class="meta-value">{{ template.language }}</span>
                        </div>
                    </div>

                    <!-- Tabs for Carousel -->
                    <div class="tabs-container" v-if="isCarousel">
                        <div class="tabs-header">
                            <div
                                v-for="(tab, idx) in carouselTabs"
                                :key="idx"
                                class="tab-item"
                                :class="{ active: activeTab === idx }"
                                @click="activeTab = idx"
                            >
                                {{ tab }}
                            </div>
                        </div>
                    </div>

                    <!-- Normal Template Configuration -->
                    <template v-if="!isCarousel">
                        <!-- Header Media/URL Selection -->
                        <div class="config-block" v-if="isMediaHeader">
                            <div class="block-header">
                                <span class="block-title">Header {{ headerComponent.format }}</span>
                                <span class="block-hint">Required</span>
                            </div>
                            
                            <!-- Media Source Selection -->
                            <div class="media-source-selection">
                                <label class="radio-option">
                                    <input type="radio" :value="false" v-model="useHeaderUrl" />
                                    <span>Upload Media</span>
                                </label>
                                <label class="radio-option">
                                    <input type="radio" :value="true" v-model="useHeaderUrl" />
                                    <span>Use URL</span>
                                </label>
                            </div>

                            <!-- URL Input -->
                            <div class="media-url-input" v-if="useHeaderUrl">
                                <input
                                    v-model="headerParams[0]"
                                    type="text"
                                    :placeholder="getTemplateHeaderAssetUrl() || getHeaderParamPlaceholder()"
                                    class="param-field"
                                />
                                <div class="url-hint" v-if="getTemplateHeaderAssetUrl() && !headerParams[0]">
                                    Template has a default media. Leave empty to use it, or enter a custom URL.
                                </div>
                            </div>

                            <!-- Upload Area -->
                            <div class="media-area" v-else>
                                <!-- Uploaded Media -->
                                <div class="media-uploaded" v-if="media">
                                    <img v-if="headerComponent.format === 'IMAGE'" :src="media.data" alt="Preview" />
                                    <video v-else-if="headerComponent.format === 'VIDEO'" :src="media.data" controls />
                                    <div v-else class="doc-preview">
                                        <a-icon type="file" />
                                        <span>{{ media.name }}</span>
                                    </div>
                                    <button class="remove-media" @click="removeMedia">
                                        <a-icon type="close" />
                                    </button>
                                </div>
                                
                                <!-- Template Default Media -->
                                <template v-else-if="getTemplateHeaderAssetUrl()">
                                    <div class="media-uploaded template-media">
                                        <img v-if="headerComponent.format === 'IMAGE'" :src="getTemplateHeaderAssetUrl()" alt="Template Media" />
                                        <video v-else-if="headerComponent.format === 'VIDEO'" :src="getTemplateHeaderAssetUrl()" controls />
                                        <div v-else class="doc-preview">
                                            <a-icon type="file" />
                                            <span>Template Media</span>
                                        </div>
                                        <div class="template-media-badge">Template Default</div>
                                    </div>
                                    <label class="upload-area small">
                                        <input
                                            type="file"
                                            :accept="getAcceptType()"
                                            @change="handleFileUpload"
                                        />
                                        <a-icon type="upload" />
                                        <span class="upload-label">Upload different media</span>
                                    </label>
                                </template>
                                
                                <!-- Upload Area -->
                                <label class="upload-area" v-else>
                                    <input
                                        type="file"
                                        :accept="getAcceptType()"
                                        @change="handleFileUpload"
                                    />
                                    <a-icon type="cloud-upload" />
                                    <span class="upload-label">Click to upload</span>
                                    <span class="upload-format">{{ getMediaHint() }}</span>
                                </label>
                            </div>
                        </div>

                        <!-- Header Text Parameters -->
                        <div class="config-block" v-if="headerComponent && headerComponent.format === 'TEXT' && headerParams.length > 0">
                            <div class="block-header">
                                <span class="block-title">Header Variables</span>
                            </div>
                            <div class="params-list">
                                <div v-for="(param, index) in headerParams" :key="'h-' + index" class="param-row">
                                    <div class="param-info">
                                        <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                        <span class="param-ctx">{{ getHeaderParamContext(index) }}</span>
                                    </div>
                                    <input
                                        v-model="headerParams[index]"
                                        type="text"
                                        :placeholder="getHeaderParamPlaceholder()"
                                        class="param-field"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="config-block" v-if="bodyParams.length > 0">
                            <div class="block-header">
                                <span class="block-title">Body Variables</span>
                            </div>
                            <div class="params-list">
                                <div v-for="(param, index) in bodyParams" :key="'b-' + index" class="param-row">
                                    <div class="param-info">
                                        <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                        <span class="param-ctx">{{ getParamContext(bodyComponent.text, index) }}</span>
                                    </div>
                                    <input
                                        v-model="bodyParams[index]"
                                        type="text"
                                        placeholder="Enter value"
                                        class="param-field"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="config-block" v-if="buttonParams.length > 0">
                            <div class="block-header">
                                <span class="block-title">Button Variables</span>
                            </div>
                            <div class="params-list">
                                <div v-for="(param, index) in buttonParams" :key="'btn-' + index" class="param-row">
                                    <div class="param-info">
                                        <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                        <span class="param-ctx">{{ getButtonParamContext(index) }}</span>
                                    </div>
                                    <input
                                        v-model="buttonParams[index]"
                                        type="text"
                                        placeholder="Enter value"
                                        class="param-field"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Limited Time Offer -->
                        <div class="config-block" v-if="limitedTimeOfferComponent">
                            <div class="block-header">
                                <span class="block-title">Limited Time Offer</span>
                                <span class="block-hint">Required</span>
                            </div>
                            <div class="params-list">
                                <div class="param-row">
                                    <div class="param-info">
                                        <span class="param-idx">
                                            <a-icon type="calendar" />
                                        </span>
                                        <span class="param-ctx">Offer expiration date</span>
                                    </div>
                                    <a-date-picker
                                        v-model="expirationDate"
                                        :show-time="true"
                                        format="YYYY-MM-DD HH:mm"
                                        placeholder="Select expiration date"
                                        style="width: 100%"
                                        :disabled-date="disabledDate"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- No Params State -->
                        <div class="ready-state" v-if="!hasAnyParams && !isMediaHeader">
                            <div class="ready-icon">
                                <a-icon type="check" />
                            </div>
                            <div class="ready-text">Ready to send</div>
                            <div class="ready-hint">This template requires no parameters</div>
                        </div>
                    </template>

                    <!-- Carousel Configuration -->
                    <template v-else>
                        <!-- Main Message Tab -->
                        <template v-if="activeTab === 0">
                            <div class="config-block" v-if="bodyParams.length > 0">
                                <div class="block-header">
                                    <span class="block-title">Body Variables</span>
                                </div>
                                <div class="params-list">
                                    <div v-for="(param, index) in bodyParams" :key="'b-' + index" class="param-row">
                                        <div class="param-info">
                                            <span class="param-idx">{{ getPlaceholder(index) }}</span>
                                            <span class="param-ctx">{{ getParamContext(bodyComponent.text, index) }}</span>
                                        </div>
                                        <input
                                            v-model="bodyParams[index]"
                                            type="text"
                                            placeholder="Enter value"
                                            class="param-field"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="ready-state" v-if="bodyParams.length === 0">
                                <div class="ready-icon">
                                    <a-icon type="check" />
                                </div>
                                <div class="ready-text">No variables</div>
                                <div class="ready-hint">Main message has no parameters</div>
                            </div>
                        </template>

                        <!-- Carousel Card Tabs -->
                        <template v-else>
                            <div
                                v-for="(card, cardIndex) in carouselCards"
                                :key="'card-' + cardIndex"
                                v-show="activeTab === cardIndex + 1"
                            >
                                <!-- Card Header Media -->
                                <div
                                    class="config-block"
                                    v-if="hasCarouselCardHeaderVariable(cardIndex)"
                                >
                                    <div class="block-header">
                                        <span class="block-title">Header {{ getCarouselCardHeaderFormat(cardIndex) }}</span>
                                        <span class="block-hint">Required</span>
                                    </div>
                                    
                                    <!-- Media Source Selection -->
                                    <div class="media-source-selection">
                                        <label class="radio-option">
                                            <input type="radio" :value="false" v-model="carouselCardUseUrl[cardIndex]" />
                                            <span>Upload Media</span>
                                        </label>
                                        <label class="radio-option">
                                            <input type="radio" :value="true" v-model="carouselCardUseUrl[cardIndex]" />
                                            <span>Use URL</span>
                                        </label>
                                    </div>

                                    <!-- URL Input -->
                                    <div class="media-url-input" v-if="carouselCardUseUrl[cardIndex]">
                                        <input
                                            v-model="carouselCardParams[cardIndex].header[0]"
                                            type="text"
                                            :placeholder="getCarouselCardAssetUrl(cardIndex) || getCarouselHeaderParamPlaceholder(cardIndex)"
                                            class="param-field"
                                        />
                                        <div class="url-hint" v-if="getCarouselCardAssetUrl(cardIndex) && !carouselCardParams[cardIndex].header[0]">
                                            Card has a default media. Leave empty to use it, or enter a custom URL.
                                        </div>
                                    </div>

                                    <!-- Upload Area -->
                                    <div class="media-area" v-else>
                                        <!-- Uploaded Media -->
                                        <div class="media-uploaded" v-if="carouselCardMedia[cardIndex]">
                                            <img v-if="getCarouselCardHeaderFormat(cardIndex) === 'IMAGE'" :src="carouselCardMedia[cardIndex].data" alt="Preview" />
                                            <video v-else-if="getCarouselCardHeaderFormat(cardIndex) === 'VIDEO'" :src="carouselCardMedia[cardIndex].data" controls />
                                            <div v-else class="doc-preview">
                                                <a-icon type="file" />
                                                <span>{{ carouselCardMedia[cardIndex].name }}</span>
                                            </div>
                                            <button class="remove-media" @click="removeCarouselCardMedia(cardIndex)">
                                                <a-icon type="close" />
                                            </button>
                                        </div>
                                        
                                        <!-- Template Default Media -->
                                        <template v-else-if="getCarouselCardAssetUrl(cardIndex)">
                                            <div class="media-uploaded template-media">
                                                <img v-if="getCarouselCardHeaderFormat(cardIndex) === 'IMAGE'" :src="getCarouselCardAssetUrl(cardIndex)" alt="Template Media" />
                                                <video v-else-if="getCarouselCardHeaderFormat(cardIndex) === 'VIDEO'" :src="getCarouselCardAssetUrl(cardIndex)" controls />
                                                <div v-else class="doc-preview">
                                                    <a-icon type="file" />
                                                    <span>Template Media</span>
                                                </div>
                                                <div class="template-media-badge">Template Default</div>
                                            </div>
                                            <label class="upload-area small">
                                                <input
                                                    type="file"
                                                    :accept="getCarouselCardAcceptType(cardIndex)"
                                                    @change="handleCarouselCardFileUpload($event, cardIndex)"
                                                />
                                                <a-icon type="upload" />
                                                <span class="upload-label">Upload different media</span>
                                            </label>
                                        </template>
                                        
                                        <!-- Upload Area -->
                                        <label class="upload-area" v-else>
                                            <input
                                                type="file"
                                                :accept="getCarouselCardAcceptType(cardIndex)"
                                                @change="handleCarouselCardFileUpload($event, cardIndex)"
                                            />
                                            <a-icon type="cloud-upload" />
                                            <span class="upload-label">Click to upload</span>
                                            <span class="upload-format">{{ getCarouselCardMediaHint(cardIndex) }}</span>
                                        </label>
                                    </div>
                                </div>

                                <!-- Card Body Variables -->
                                <div
                                    class="config-block"
                                    v-if="carouselCardParams[cardIndex].body.length > 0"
                                >
                                    <div class="block-header">
                                        <span class="block-title">Body Variables</span>
                                    </div>
                                    <div class="params-list">
                                        <div
                                            v-for="(param, paramIndex) in carouselCardParams[cardIndex].body"
                                            :key="'body-' + paramIndex"
                                            class="param-row"
                                        >
                                            <div class="param-info">
                                                <span class="param-idx">{{ getPlaceholder(paramIndex) }}</span>
                                                <span class="param-ctx">{{ getCarouselCardBodyContext(cardIndex, paramIndex) }}</span>
                                            </div>
                                            <input
                                                v-model="carouselCardParams[cardIndex].body[paramIndex]"
                                                type="text"
                                                placeholder="Enter value"
                                                class="param-field"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <!-- Card Button Variables -->
                                <div
                                    class="config-block"
                                    v-if="carouselCardParams[cardIndex].buttons.length > 0"
                                >
                                    <div class="block-header">
                                        <span class="block-title">Button Variables</span>
                                    </div>
                                    <div class="params-list">
                                        <div
                                            v-for="(param, paramIndex) in carouselCardParams[cardIndex].buttons"
                                            :key="'btn-' + paramIndex"
                                            class="param-row"
                                        >
                                            <div class="param-info">
                                                <span class="param-idx">{{ getPlaceholder(paramIndex) }}</span>
                                                <span class="param-ctx">URL suffix</span>
                                            </div>
                                            <input
                                                v-model="carouselCardParams[cardIndex].buttons[paramIndex]"
                                                type="text"
                                                placeholder="Enter value"
                                                class="param-field"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="ready-state"
                                    v-if="carouselCardParams[cardIndex].header.length === 0 &&
                                          carouselCardParams[cardIndex].body.length === 0 &&
                                          carouselCardParams[cardIndex].buttons.length === 0"
                                >
                                    <div class="ready-icon">
                                        <a-icon type="check" />
                                    </div>
                                    <div class="ready-text">No variables</div>
                                    <div class="ready-hint">This card has no parameters</div>
                                </div>
                            </div>
                        </template>
                    </template>
                </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
                <button class="btn-cancel" @click="$emit('close')">Cancel</button>
                <button class="btn-send" @click="sendMessage" :disabled="sending">
                    <a-spin v-if="sending" size="small" />
                    <template v-else>
                        <a-icon type="send" />
                        <span>Send</span>
                    </template>
                </button>
            </div>
        </div>
    </Modal2>
</template>

<script>
import Modal2 from "../../../../../common/Modal2.vue";
import WhatsappMessagePreview from "../../../../../../views/Pages/WhatsappBusinessGupshup/WhatsappMessagePreview.vue";
import { mapState } from "vuex";
import { uploadAsset } from "../../../../../../api/assetUpload";
import axios from "axios";

export default {
    name: "MetaTemplateMessageModal",
    components: { Modal2, WhatsappMessagePreview },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        template: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            sending: false,
            media: null,
            asset: null,
            headerParams: [],
            bodyParams: [],
            buttonParams: [],
            activeTab: 0,
            carouselCardParams: [],
            useHeaderUrl: false, // false = upload, true = URL
            carouselCardMedia: [], // Array of media objects for each carousel card
            carouselCardUseUrl: [], // Array of boolean for each carousel card
            expirationDate: null, // For limited time offer
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
        }),
        components() {
            return this.template.components || [];
        },
        headerComponent() {
            return this.components.find((c) => c.type === "HEADER");
        },
        bodyComponent() {
            return this.components.find((c) => c.type === "BODY");
        },
        footerComponent() {
            return this.components.find((c) => c.type === "FOOTER");
        },
        buttonsComponent() {
            return this.components.find((c) => c.type === "BUTTONS");
        },
        limitedTimeOfferComponent() {
            return this.components.find((c) => c.type === "LIMITED_TIME_OFFER");
        },
        carouselComponent() {
            return this.components.find((c) => c.type === "CAROUSEL");
        },
        isCarousel() {
            return !!this.carouselComponent;
        },
        carouselCards() {
            if (!this.carouselComponent || !this.carouselComponent.cards) return [];
            return this.carouselComponent.cards;
        },
        carouselTabs() {
            const tabs = ["Main Message"];
            this.carouselCards.forEach((_, index) => {
                tabs.push(`Card ${index + 1}`);
            });
            return tabs;
        },
        isMediaHeader() {
            if (!this.headerComponent) return false;
            return ["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format);
        },
        hasAnyParams() {
            if (this.isCarousel) {
                return this.bodyParams.length > 0 || 
                       this.limitedTimeOfferComponent ||
                       this.carouselCardParams.some(card =>
                           card.header.length > 0 || card.body.length > 0 || card.buttons.length > 0
                       );
            }
            return this.headerParams.length > 0 || 
                   this.bodyParams.length > 0 || 
                   this.buttonParams.length > 0 || 
                   !!this.limitedTimeOfferComponent;
        },
        // Preview computed properties
        previewTemplateType() {
            if (this.isCarousel) return "CAROUSEL";
            return this.template.category || "CUSTOM_MESSAGE";
        },
        previewHeaderMediaType() {
            if (!this.headerComponent) return null;
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format)) {
                return this.headerComponent.format;
            }
            return null;
        },
        previewHeaderMediaURL() {
            // Priority: URL variable > uploaded media > template default media
            if (this.headerParams.length > 0 && this.headerParams[0]) {
                return this.headerParams[0];
            }
            if (this.media) return this.media.data;
            
            // Template default media
            const headerAssetCdn = this.template?.mediaPayload?.headerAsset?.cdnUrl;
            if (headerAssetCdn) {
                return headerAssetCdn;
            }
            
            // Fallback to template.cdnUrl (for backward compatibility)
            if (this.template?.cdnUrl) {
                return this.template.cdnUrl;
            }
            
            return null;
        },
        previewHeaderText() {
            if (!this.headerComponent || this.headerComponent.format !== "TEXT") return "";
            let text = this.headerComponent.text || "";
            this.headerParams.forEach((value, index) => {
                const placeholder = "{{" + (index + 1) + "}}";
                text = text.replace(placeholder, value || "[" + (index + 1) + "]");
            });
            return text;
        },
        previewBody() {
            if (!this.bodyComponent) return "";
            let text = this.bodyComponent.text || "";
            this.bodyParams.forEach((value, index) => {
                const placeholder = "{{" + (index + 1) + "}}";
                text = text.replace(placeholder, value || "[" + (index + 1) + "]");
            });
            return text;
        },
        previewFooter() {
            return this.footerComponent ? this.footerComponent.text : null;
        },
        previewButtons() {
            if (!this.buttonsComponent) return [];
            const buttons = this.buttonsComponent.buttons || [];
            
            // COPY_CODE button'larına parametre değerlerini ekle
            let buttonParamIndex = 0;
            return buttons.map(btn => {
                const button = { ...btn };
                
                if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
                    // URL button parametreleri
                    const matches = btn.url.match(/\{\{\d+\}\}/g);
                    if (matches && buttonParamIndex < this.buttonParams.length) {
                        let url = btn.url;
                        matches.forEach(() => {
                            const paramValue = this.buttonParams[buttonParamIndex] || "[suffix]";
                            url = url.replace(/\{\{\d+\}\}/, paramValue);
                            buttonParamIndex++;
                        });
                        button.url = url;
                    }
                } else if (btn.type === "COPY_CODE") {
                    // COPY_CODE button parametresi - preview'da sadece text gösterilir
                    button.text = "Copy code"; // COPY_CODE button text'i sabit
                    if (buttonParamIndex < this.buttonParams.length) {
                        const paramValue = this.buttonParams[buttonParamIndex];
                        // Parametre değerini code olarak sakla ama preview'da gösterilmez
                        button.code = paramValue || "";
                        buttonParamIndex++;
                    }
                }
                
                return button;
            });
        },
        previewLimitedTimeOffer() {
            if (!this.limitedTimeOfferComponent) return null;
            
            const lto = {
                text: this.limitedTimeOfferComponent.limited_time_offer?.text || "",
                hasExpiration: this.limitedTimeOfferComponent.limited_time_offer?.has_expiration !== false,
            };
            
            // Expiration date ekle
            if (this.expirationDate) {
                lto.expirationDate = this.expirationDate;
            }
            
            return lto;
        },
        previewCards() {
            if (!this.isCarousel) return [];
            return this.carouselCards.map((card, cardIndex) => {
                const cardComponents = card.components || [];
                const headerComp = cardComponents.find(c => c.type === "HEADER");
                const bodyComp = cardComponents.find(c => c.type === "BODY");
                const buttonsComp = cardComponents.find(c => c.type === "BUTTONS");

                let mediaUrl = null;
                let headerType = null;

                // Get header media
                if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                    headerType = headerComp.format;
                    const cardParam = this.carouselCardParams[cardIndex];
                    
                    // Priority: URL variable > uploaded media > card default media
                    if (cardParam && cardParam.header && cardParam.header.length > 0 && cardParam.header[0]) {
                        mediaUrl = cardParam.header[0];
                    } else if (this.carouselCardMedia[cardIndex]) {
                        mediaUrl = this.carouselCardMedia[cardIndex].data;
                    } else {
                        // Card's default media from template
                        const cardAssetCdn = this.template?.mediaPayload?.cardAssets?.[cardIndex]?.cdnUrl;
                        if (cardAssetCdn) {
                            mediaUrl = cardAssetCdn;
                        }
                    }
                }

                // Get body text with variables
                let sampleText = "";
                if (bodyComp && bodyComp.text) {
                    sampleText = bodyComp.text;
                    const cardParam = this.carouselCardParams[cardIndex];
                    if (cardParam && cardParam.body) {
                        cardParam.body.forEach((value, index) => {
                            const placeholder = "{{" + (index + 1) + "}}";
                            sampleText = sampleText.replace(placeholder, value || "[" + (index + 1) + "]");
                        });
                    }
                }

                // Button parametrelerini işle
                let previewButtons = [];
                if (buttonsComp && buttonsComp.buttons) {
                    const cardParam = this.carouselCardParams[cardIndex];
                    let buttonParamIndex = 0;
                    
                    previewButtons = buttonsComp.buttons.map(btn => {
                        const button = { ...btn };
                        
                        if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
                            const matches = btn.url.match(/\{\{\d+\}\}/g);
                            if (matches && cardParam.buttons) {
                                let url = btn.url;
                                matches.forEach(() => {
                                    if (buttonParamIndex < cardParam.buttons.length) {
                                        const paramValue = cardParam.buttons[buttonParamIndex] || "[suffix]";
                                        url = url.replace(/\{\{\d+\}\}/, paramValue);
                                        buttonParamIndex++;
                                    }
                                });
                                button.url = url;
                            }
                        } else if (btn.type === "COPY_CODE") {
                            button.text = "Copy code"; // COPY_CODE button text'i sabit
                            // Parametre değeri backend'e gönderilecek ama preview'da gösterilmez
                            if (cardParam.buttons && buttonParamIndex < cardParam.buttons.length) {
                                buttonParamIndex++;
                            }
                        }
                        
                        return button;
                    });
                }

                return {
                    mediaUrl,
                    headerType,
                    sampleText,
                    buttons: previewButtons
                };
            });
        },
    },
    mounted() {
        this.initializeParams();
    },
    methods: {
        getPlaceholder(index) {
            return "{{" + (index + 1) + "}}";
        },
        getButtonParamContext(index) {
            if (!this.buttonsComponent || !this.buttonsComponent.buttons) return "URL suffix";
            let paramIndex = 0;
            for (const btn of this.buttonsComponent.buttons) {
                if (btn.type === "URL" && btn.url) {
                    const matches = btn.url.match(/\{\{\d+\}\}/g);
                    if (matches) {
                        for (let i = 0; i < matches.length; i++) {
                            if (paramIndex === index) {
                                return `URL suffix (${btn.text || 'Button'})`;
                            }
                            paramIndex++;
                        }
                    }
                } else if (btn.type === "COPY_CODE") {
                    if (paramIndex === index) {
                        return `Copy code (${btn.text || 'Button'})`;
                    }
                    paramIndex++;
                }
            }
            return "URL suffix";
        },
        initializeParams() {
            if (this.isCarousel) {
                // Initialize main message body params
                if (this.bodyComponent && this.bodyComponent.text) {
                    const matches = this.bodyComponent.text.match(/\{\{\d+\}\}/g);
                    this.bodyParams = matches ? matches.map(() => "") : [];
                }

                // Initialize carousel card arrays with proper reactivity
                this.carouselCardMedia = [];
                this.carouselCardUseUrl = [];
                for (let i = 0; i < this.carouselCards.length; i++) {
                    this.$set(this.carouselCardMedia, i, null);
                    this.$set(this.carouselCardUseUrl, i, false);
                }
                
                // Initialize carousel card params
                this.carouselCardParams = this.carouselCards.map(card => {
                    const cardComponents = card.components || [];
                    const headerComp = cardComponents.find(c => c.type === "HEADER");
                    const bodyComp = cardComponents.find(c => c.type === "BODY");
                    const buttonsComp = cardComponents.find(c => c.type === "BUTTONS");

                    const cardParam = {
                        header: [],
                        body: [],
                        buttons: [],
                    };

                    // Check header media variable (IMAGE/VIDEO/DOCUMENT)
                    if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                        // Header media can have URL variable for carousel
                        cardParam.header = [""];
                    }

                    // Check body variables
                    if (bodyComp && bodyComp.text) {
                        const matches = bodyComp.text.match(/\{\{\d+\}\}/g);
                        cardParam.body = matches ? matches.map(() => "") : [];
                    }

                    // Check button variables
                    if (buttonsComp && buttonsComp.buttons) {
                        let buttonParamCount = 0;
                        buttonsComp.buttons.forEach(btn => {
                            if (btn.type === "URL" && btn.url) {
                                const matches = btn.url.match(/\{\{\d+\}\}/g);
                                if (matches) buttonParamCount += matches.length;
                            } else if (btn.type === "COPY_CODE") {
                                // COPY_CODE button'ları için her button başına 1 parametre
                                buttonParamCount += 1;
                            }
                        });
                        cardParam.buttons = new Array(buttonParamCount).fill("");
                    }

                    return cardParam;
                });
            } else {
                // Normal template initialization
                if (this.headerComponent) {
                    if (this.headerComponent.text) {
                        // TEXT header with variables
                        const matches = this.headerComponent.text.match(/\{\{\d+\}\}/g);
                        this.headerParams = matches ? matches.map(() => "") : [];
                    } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format)) {
                        // MEDIA header always has option for URL variable
                        this.headerParams = [""];
                    }
                }

                if (this.bodyComponent && this.bodyComponent.text) {
                    const matches = this.bodyComponent.text.match(/\{\{\d+\}\}/g);
                    this.bodyParams = matches ? matches.map(() => "") : [];
                }

                if (this.buttonsComponent && this.buttonsComponent.buttons) {
                    let buttonParamCount = 0;
                    this.buttonsComponent.buttons.forEach((btn) => {
                        if (btn.type === "URL" && btn.url) {
                            const matches = btn.url.match(/\{\{\d+\}\}/g);
                            if (matches) buttonParamCount += matches.length;
                        } else if (btn.type === "COPY_CODE") {
                            // COPY_CODE button'ları için her button başına 1 parametre
                            buttonParamCount += 1;
                        }
                    });
                    this.buttonParams = new Array(buttonParamCount).fill("");
                }
            }
        },
        getParamContext(text, index) {
            if (!text) return "";
            const placeholder = "{{" + (index + 1) + "}}";
            const position = text.indexOf(placeholder);
            if (position === -1) return "";
            
            const start = Math.max(0, position - 20);
            const end = Math.min(text.length, position + placeholder.length + 20);
            let context = text.substring(start, end);
            
            if (start > 0) context = "..." + context;
            if (end < text.length) context = context + "...";
            
            return context;
        },
        getHeaderParamPlaceholder() {
            if (!this.headerComponent) return "Enter value";
            
            // For MEDIA header
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerComponent.format)) {
                const type = this.headerComponent.format.toLowerCase();
                return `Enter ${type} URL`;
            }
            
            return "Enter value";
        },
        getTemplateHeaderAssetUrl() {
            const headerAssetCdn = this.template?.mediaPayload?.headerAsset?.cdnUrl;
            if (headerAssetCdn) return headerAssetCdn;
            
            // Fallback to template.cdnUrl (for backward compatibility)
            if (this.template?.cdnUrl) return this.template.cdnUrl;
            
            return null;
        },
        getCarouselCardAssetUrl(cardIndex) {
            const cardAssetCdn = this.template?.mediaPayload?.cardAssets?.[cardIndex]?.cdnUrl;
            return cardAssetCdn || null;
        },
        // Carousel methods
        hasCarouselCardHeaderVariable(cardIndex) {
            if (!this.carouselCards[cardIndex]) return false;
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            return headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format);
        },
        getCarouselCardBodyContext(cardIndex, paramIndex) {
            if (!this.carouselCards[cardIndex]) return "";
            const card = this.carouselCards[cardIndex];
            const bodyComp = (card.components || []).find(c => c.type === "BODY");
            if (!bodyComp || !bodyComp.text) return "";
            return this.getParamContext(bodyComp.text, paramIndex);
        },
        getCarouselHeaderParamContext(cardIndex) {
            if (!this.carouselCards[cardIndex]) return "";
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            if (!headerComp) return "";
            
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                return `${headerComp.format} URL`;
            }
            
            return "";
        },
        getCarouselHeaderParamPlaceholder(cardIndex) {
            if (!this.carouselCards[cardIndex]) return "Enter value";
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            if (!headerComp) return "Enter value";
            
            if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
                const type = headerComp.format.toLowerCase();
                return `Enter ${type} URL`;
            }
            
            return "Enter value";
        },
        getCarouselCardHeaderFormat(cardIndex) {
            if (!this.carouselCards[cardIndex]) return "";
            const card = this.carouselCards[cardIndex];
            const headerComp = (card.components || []).find(c => c.type === "HEADER");
            return headerComp ? headerComp.format : "";
        },
        getAcceptType() {
            if (!this.headerComponent) return "";
            const format = this.headerComponent.format;
            if (format === "IMAGE") return "image/jpeg, image/png";
            if (format === "VIDEO") return "video/mp4";
            if (format === "DOCUMENT") return "application/pdf";
            return "";
        },
        getMediaHint() {
            if (!this.headerComponent) return "";
            const format = this.headerComponent.format;
            if (format === "IMAGE") return "JPEG, PNG • Max 5MB";
            if (format === "VIDEO") return "MP4 • Max 16MB";
            if (format === "DOCUMENT") return "PDF • Max 100MB";
            return "";
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            const mb = file.size / 1024 / 1024;
            const format = this.headerComponent.format;

            if (format === "IMAGE" && mb > 5) {
                this.$message.error("Image must be less than 5MB");
                return;
            }
            if (format === "VIDEO" && mb > 16) {
                this.$message.error("Video must be less than 16MB");
                return;
            }
            if (format === "DOCUMENT" && mb > 100) {
                this.$message.error("Document must be less than 100MB");
                return;
            }

            this.$message.loading("Uploading...", 0);

            try {
                const path = this.siteId + "/whatsapp-business/" + this.channel._id + "/assets/";
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "meta-waba-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const _asset = uploadData.data.asset;
                const cdnUrl = _asset.v2Format && _asset.cdnUrl
                    ? _asset.cdnUrl
                    : "https://static.supsis.live/" + _asset.path + _asset.id + "." + _asset.ext;

                this.asset = {
                    id: _asset.id,
                    url: cdnUrl,
                    filename: file.name,
                    type: file.type,
                };

                this.media = {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                };

                // Set CDN URL to header params
                this.headerParams[0] = cdnUrl;

                this.$message.destroy();
                this.$message.success("File uploaded successfully");
            } catch (error) {
                this.$message.destroy();
                this.$message.error("Upload failed");
                console.error(error);
            }
        },
        removeMedia() {
            this.media = null;
            this.asset = null;
            this.headerParams[0] = "";
        },
        async handleCarouselCardFileUpload(event, cardIndex) {
            const file = event.target.files[0];
            if (!file) return;

            const mb = file.size / 1024 / 1024;
            const format = this.getCarouselCardHeaderFormat(cardIndex);

            if (format === "IMAGE" && mb > 5) {
                this.$message.error("Image must be less than 5MB");
                return;
            }
            if (format === "VIDEO" && mb > 16) {
                this.$message.error("Video must be less than 16MB");
                return;
            }
            if (format === "DOCUMENT" && mb > 100) {
                this.$message.error("Document must be less than 100MB");
                return;
            }

            this.$message.loading("Uploading...", 0);

            try {
                const path = this.siteId + "/whatsapp-business/" + this.channel._id + "/assets/";
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "meta-waba-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const _asset = uploadData.data.asset;
                const cdnUrl = _asset.v2Format && _asset.cdnUrl
                    ? _asset.cdnUrl
                    : "https://static.supsis.live/" + _asset.path + _asset.id + "." + _asset.ext;

                // Set carousel card media
                this.$set(this.carouselCardMedia, cardIndex, {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                });

                // Set CDN URL to carousel card header params
                this.carouselCardParams[cardIndex].header[0] = cdnUrl;

                this.$message.destroy();
                this.$message.success("File uploaded successfully");
            } catch (error) {
                this.$message.destroy();
                this.$message.error("Upload failed");
                console.error(error);
            }
        },
        removeCarouselCardMedia(cardIndex) {
            this.$set(this.carouselCardMedia, cardIndex, null);
            this.carouselCardParams[cardIndex].header[0] = "";
        },
        getCarouselCardAcceptType(cardIndex) {
            const format = this.getCarouselCardHeaderFormat(cardIndex);
            if (format === "IMAGE") return "image/jpeg, image/png";
            if (format === "VIDEO") return "video/mp4";
            if (format === "DOCUMENT") return "application/pdf";
            return "";
        },
        getCarouselCardMediaHint(cardIndex) {
            const format = this.getCarouselCardHeaderFormat(cardIndex);
            if (format === "IMAGE") return "JPEG, PNG • Max 5MB";
            if (format === "VIDEO") return "MP4 • Max 16MB";
            if (format === "DOCUMENT") return "PDF • Max 100MB";
            return "";
        },
        sendMessage() {
            // Validate LTO expiration date
            if (this.limitedTimeOfferComponent && !this.expirationDate) {
                this.$message.warning("Please select an expiration date for the limited time offer");
                return;
            }

            this.sending = true;

            const payload = {
                template: this.template,
                format: "meta",
            };

            if (this.isCarousel) {
                payload.params = {
                    body: this.bodyParams,
                    carousel: this.carouselCardParams.map(card => ({
                        header: card.header,
                        body: card.body,
                        buttons: card.buttons,
                    })),
                };
            } else {
                payload.params = {
                    header: this.headerParams.filter(p => p), // Only send non-empty values
                    body: this.bodyParams,
                    button: this.buttonParams,
                };
            }

            // Add expiration time for LTO
            if (this.limitedTimeOfferComponent && this.expirationDate) {
                payload.params.expirationTimeMs = this.expirationDate.valueOf();
            }

            this.$emit("send", payload);
        },
        disabledDate(current) {
            // Disable past dates
            return current && current < this.$moment().startOf('day');
        },
    },
    watch: {
        template: {
            handler() {
                this.initializeParams();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.meta-template-modal {
    display: flex;
    flex-direction: column;

    .modal-body {
        display: flex;
        gap: 0;
        background: #f5f5f5;
        min-height: 500px;
        max-height: 70vh;
        border-radius: 8px 8px 0 0;
        overflow: hidden;
    }

    .preview-section {
        width: 400px;
        flex-shrink: 0;
        background: #fff;
        display: flex;
        flex-direction: column;
        border-right: 1px solid #e8e8e8;
    }

    .config-section {
        flex: 1;
        background: #fafafa;
        overflow-y: auto;
        display: flex;
        flex-direction: column;

        .meta-block {
            padding: 20px;
            border-bottom: 1px solid #e8e8e8;
            display: flex;
            gap: 24px;
            background: #fff;

            .meta-item {
                display: flex;
                flex-direction: column;
                gap: 4px;

                .meta-label {
                    font-size: 11px;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    color: #8c8c8c;
                }

                .meta-value {
                    font-size: 13px;
                    font-weight: 500;
                    color: #262626;

                    &.meta-tag {
                        display: inline-block;
                        background: #e6f4ff;
                        color: #0958d9;
                        padding: 2px 8px;
                        font-size: 11px;
                        font-weight: 600;
                    }
                }
            }
        }

        .tabs-container {
            border-bottom: 1px solid #f0f0f0;

            .tabs-header {
                display: flex;
                gap: 0;
                overflow-x: auto;

                .tab-item {
                    padding: 12px 16px;
                    font-size: 13px;
                    font-weight: 500;
                    color: #595959;
                    cursor: pointer;
                    border-bottom: 2px solid transparent;
                    transition: all 0.2s;
                    white-space: nowrap;
                    flex-shrink: 0;

                    &:hover {
                        color: #1677ff;
                    }

                    &.active {
                        color: #1677ff;
                        border-bottom-color: #1677ff;
                    }
                }
            }
        }

        .config-block {
            padding: 20px;
            border-bottom: 1px solid #e8e8e8;
            background: #fff;
            margin-bottom: 1px;

            .block-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;

                .block-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #262626;
                }

                .block-hint {
                    font-size: 11px;
                    color: #8c8c8c;
                    background: #f0f0f0;
                    padding: 2px 8px;
                    border-radius: 4px;
                }
            }

            .media-source-selection {
                display: flex;
                gap: 8px;
                margin-bottom: 16px;
                padding: 4px;
                background: #fafafa;
                border-radius: 8px;

                .radio-option {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    font-size: 13px;
                    color: #595959;
                    padding: 8px 12px;
                    border-radius: 6px;
                    transition: all 0.2s;

                    input[type="radio"] {
                        cursor: pointer;
                        width: 16px;
                        height: 16px;
                    }

                    span {
                        font-weight: 500;
                    }

                    &:hover {
                        color: #1677ff;
                        background: #f0f5ff;
                    }
                }
            }

            .media-url-input {
                margin-bottom: 12px;

                input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #d9d9d9;
                    border-radius: 4px;
                    font-size: 13px;
                    outline: none;
                    transition: border-color 0.15s;

                    &:focus {
                        border-color: #1677ff;
                        box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
                    }

                    &::placeholder {
                        color: #bfbfbf;
                    }
                }

                .url-hint {
                    margin-top: 6px;
                    font-size: 11px;
                    color: #8c8c8c;
                    line-height: 1.4;
                }
            }

            .media-area {
                .media-uploaded {
                    position: relative;
                    background: #fafafa;
                    border: 1px solid #e8e8e8;
                    border-radius: 8px;
                    overflow: hidden;

                    img, video {
                        width: 100%;
                        max-height: 200px;
                        object-fit: cover;
                        display: block;
                    }

                    .doc-preview {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding: 24px 16px;
                        background: linear-gradient(135deg, #f0f5ff 0%, #fafafa 100%);

                        .anticon {
                            font-size: 32px;
                            color: #1677ff;
                        }

                        span {
                            font-size: 14px;
                            font-weight: 500;
                            color: #262626;
                        }
                    }

                    .remove-media {
                        position: absolute;
                        top: 8px;
                        right: 8px;
                        width: 28px;
                        height: 28px;
                        background: rgba(0, 0, 0, 0.6);
                        border: none;
                        border-radius: 50%;
                        color: #fff;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s;

                        &:hover {
                            background: rgba(255, 77, 79, 0.9);
                            transform: scale(1.1);
                        }

                        .anticon {
                            font-size: 14px;
                        }
                    }

                    &.template-media {
                        border: 2px solid #91d5ff;
                        background: #f0f5ff;

                        .template-media-badge {
                            position: absolute;
                            top: 12px;
                            left: 12px;
                            background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
                            color: #fff;
                            padding: 6px 12px;
                            font-size: 11px;
                            font-weight: 600;
                            border-radius: 6px;
                            box-shadow: 0 2px 8px rgba(22, 119, 255, 0.3);
                            letter-spacing: 0.3px;
                        }
                    }
                }

                .upload-area {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    padding: 32px 24px;
                    border: 2px dashed #d9d9d9;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s;
                    background: #fafafa;

                    &:hover {
                        border-color: #1677ff;
                        background: #f0f5ff;
                        
                        .anticon {
                            color: #1677ff;
                            transform: scale(1.1);
                        }
                    }

                    input {
                        display: none;
                    }

                    .anticon {
                        font-size: 36px;
                        color: #8c8c8c;
                        transition: all 0.2s;
                    }

                    .upload-label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #262626;
                    }

                    .upload-format {
                        font-size: 12px;
                        color: #8c8c8c;
                    }

                    &.small {
                        margin-top: 12px;
                        padding: 16px 20px;
                        flex-direction: row;
                        justify-content: center;
                        border: 1px dashed #d9d9d9;
                        background: #fff;

                        .anticon {
                            font-size: 18px;
                            color: #595959;
                        }

                        .upload-label {
                            font-size: 13px;
                            color: #595959;
                        }

                        &:hover {
                            border-color: #1677ff;
                            background: #f0f5ff;

                            .anticon,
                            .upload-label {
                                color: #1677ff;
                            }
                        }
                    }
                }
            }

            .params-list {
                display: flex;
                flex-direction: column;
                gap: 16px;

                .param-row {
                    .param-info {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        margin-bottom: 8px;

                        .param-idx {
                            font-size: 12px;
                            font-weight: 700;
                            color: #1677ff;
                            font-family: 'Monaco', 'Menlo', monospace;
                            background: #e6f4ff;
                            padding: 2px 6px;
                            border-radius: 4px;
                        }

                        .param-ctx {
                            font-size: 12px;
                            color: #8c8c8c;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                    }

                    .param-field {
                        width: 100%;
                        padding: 10px 12px;
                        border: 1px solid #d9d9d9;
                        border-radius: 6px;
                        font-size: 13px;
                        outline: none;
                        transition: all 0.2s;
                        background: #fff;

                        &:hover {
                            border-color: #40a9ff;
                        }

                        &:focus {
                            border-color: #1677ff;
                            box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
                        }

                        &::placeholder {
                            color: #bfbfbf;
                        }
                    }
                }

                .url-hint {
                    margin-top: 6px;
                    font-size: 11px;
                    color: #8c8c8c;
                    line-height: 1.4;
                }
            }
        }

        .ready-state {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 40px;

            .ready-icon {
                width: 56px;
                height: 56px;
                background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
                border: 2px solid #b7eb8f;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 20px;
                box-shadow: 0 4px 12px rgba(82, 196, 26, 0.15);

                .anticon {
                    font-size: 28px;
                    color: #52c41a;
                }
            }

            .ready-text {
                font-size: 16px;
                font-weight: 600;
                color: #262626;
            }

            .ready-hint {
                font-size: 13px;
                color: #8c8c8c;
                margin-top: 6px;
            }
        }
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 16px 20px;
        background: #fff;
        border-top: 1px solid #f0f0f0;

        button {
            padding: 10px 24px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            gap: 8px;
            border-radius: 6px;
        }

        .btn-cancel {
            background: #fff;
            border: 1px solid #d9d9d9;
            color: #595959;

            &:hover {
                border-color: #1677ff;
                color: #1677ff;
                background: #f0f5ff;
            }
        }

        .btn-send {
            background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
            border: none;
            color: #fff;
            box-shadow: 0 2px 8px rgba(22, 119, 255, 0.3);

            &:hover:not(:disabled) {
                background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
                box-shadow: 0 4px 12px rgba(22, 119, 255, 0.4);
                transform: translateY(-1px);
            }

            &:active:not(:disabled) {
                transform: translateY(0);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
                box-shadow: none;
            }

            .anticon {
                font-size: 16px;
            }
        }
    }
}
</style>

<style lang="scss">
.meta-template-message-modal {
    .ant-modal {
        max-width: 90vw;
        min-width: 90vw !important;
    }

    .ant-modal-body {
        padding: 0 !important;
    }

    .ant-modal-header {
        padding: 16px 20px;
        border-bottom: 1px solid #f0f0f0;
    }

    .ant-modal-title {
        font-size: 15px;
        font-weight: 600;
    }

    .ant-calendar-picker {
        width: 100%;
    }
    
    .ant-calendar-picker-input {
        padding: 10px 12px;
        border-radius: 6px;
        font-size: 13px;
        
        &:hover {
            border-color: #40a9ff;
        }
    }
}
</style>
